import {createApi} from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import _ from 'lodash';
import {getSharedDocumentHeader} from './apiUtils';
import {selectEffectiveOrg} from './session';

const axiosBaseQuery =
  ({baseUrl} = {baseUrl: ''}) =>
  async ({url, method = 'GET', data, params, headers}) => {
    // Lazy require breaks the `store → disclosureReviewApi → store` cycle at
    // module load. Hooks that import this slice's selectors can now do so
    // without TDZ'ing on `reducerPath` during test-harness cold loads.
    const store = require('../store').default;
    const state = store.getState();

    const {session} = state;
    const effectiveOrg = selectEffectiveOrg(state);

    const _headers = {
      'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
      ...getSharedDocumentHeader(),
      ...headers,
    };
    if (!_.isNil(session.accessToken)) {
      _headers.Authorization = `Bearer ${session.accessToken}`;
    }
    try {
      return await axios({
        url: `${baseUrl}${url}`,
        method,
        data,
        params,
        headers: _headers,
      });
    } catch (axiosError) {
      const err = axiosError;
      return {
        error: {
          code: err.code,
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const disclosureReviewApiSlice = createApi({
  reducerPath: 'disclosureReviewApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_COPILOT_V2_API_URL,
  }),
  endpoints: (builder) => ({
    getAllAvailableDisclosures: builder.query({
      query: ({documentId}) => ({
        url: `/disclosures/${documentId}`,
        method: 'GET',
      }),
    }),
    generateDisclosure: builder.mutation({
      query: ({documentId, data}) => ({
        url: `/disclosures/${documentId}/generate`,
        method: 'POST',
        data,
      }),
    }),
    dismissTag: builder.mutation({
      query: ({documentId, tagId}) => ({
        url: `/disclosures/${documentId}/dismiss/${tagId}`,
        method: 'POST',
        data: {},
      }),
    }),
    undismissTag: builder.mutation({
      query: ({documentId, tagId}) => ({
        url: `/disclosures/${documentId}/undismiss/${tagId}`,
        method: 'POST',
        data: {},
      }),
    }),
  }),
});

export const {
  endpoints,
  useGetAllAvailableDisclosuresQuery,
  useGenerateDisclosureMutation,
  useDismissTagMutation,
  useUndismissTagMutation,
} = disclosureReviewApiSlice;
